'use client';

import { useEffect, useState } from 'react';
import AppImage from '@/components/ui/AppImage';
import Icon from '@/components/ui/AppIcon';
import { useLocale, useLocalizedPath } from '@/context/LocaleContext';
import { getPublicStrings } from '@/i18n/public';
import { getBookingUrl } from '@/lib/booking';

export default function HeroSection() {
  const [isVisible, setIsVisible] = useState(false);
  const locale = useLocale();
  const localizePath = useLocalizedPath();
  const copy = getPublicStrings(locale).home.hero;
  const primaryHref = getBookingUrl('hero');
  const primaryExternal = primaryHref.startsWith('http');

  useEffect(() => {
    setIsVisible(true);
  }, []);

  return (
    <section
      id="hero"
      className="relative flex min-h-screen items-center overflow-hidden border-b border-border bg-background pb-16 pt-32 md:pb-24 md:pt-40 px-6 md:px-12"
    >
      <div className="absolute inset-0">
        <AppImage
          src="/assets/images/hero-3Z4A0422.jpg"
          alt={copy.imageAlt}
          fill
          priority
          sizes="100vw"
          className="h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-black/45 md:bg-black/35" />
        <div className="absolute inset-0 bg-gradient-to-r from-background via-background/95 to-background/70 md:via-background/85 md:to-background/40" />
        <div className="absolute inset-0 bg-gradient-to-b from-background/10 via-background/35 to-background/85 md:hidden" />
        <div className="absolute inset-0 bg-gradient-to-l from-background/70 via-transparent to-transparent md:hidden" />
      </div>

      <div className="relative z-10 mx-auto grid w-full max-w-7xl grid-cols-1 items-end gap-12 lg:grid-cols-12">
        <div className="lg:col-span-8">
          <div
            className={`mb-8 flex items-center gap-4 transition-all duration-800 ${
              isVisible ? 'fade-in-up' : 'fade-in-up-init'
            }`}
          >
            <div className="h-[1px] w-12 bg-gold" />
            <span className="text-[10px] font-bold uppercase tracking-[0.3em] text-foreground/85 drop-shadow-[0_1px_2px_rgba(0,0,0,0.45)]">
              {copy.eyebrow}
            </span>
          </div>

          <h1
            className={`mb-8 font-display text-5xl font-semibold leading-[0.95] tracking-tight text-foreground drop-shadow-[0_2px_8px_rgba(0,0,0,0.45)] transition-all duration-800 delay-100 md:text-7xl lg:text-8xl ${
              isVisible ? 'fade-in-up' : 'fade-in-up-init'
            }`}
          >
            {copy.title}
          </h1>
        </div>

        <div
          className={`rounded-xl border border-border/60 bg-background/75 p-4 backdrop-blur-sm transition-all duration-800 delay-200 sm:p-5 lg:col-span-4 lg:border-0 lg:bg-transparent lg:p-0 lg:backdrop-blur-0 ${
            isVisible ? 'fade-in-up' : 'fade-in-up-init'
          }`}
        >
          <p className="mb-10 max-w-md text-base font-light leading-relaxed text-foreground/95 drop-shadow-[0_1px_2px_rgba(0,0,0,0.4)] md:text-lg">
            {copy.subtitle}
          </p>

          <div className="mb-8 flex flex-col gap-4 sm:flex-row">
            <a
              href={primaryHref}
              {...(primaryExternal ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
              className="group flex min-h-[48px] items-center justify-center gap-3 bg-gold px-8 py-4 text-sm font-bold uppercase tracking-widest text-gold-foreground transition-all hover:bg-gold/90 hover:shadow-xl hover:shadow-gold/20"
            >
              {copy.primaryCta}
              <Icon
                name="ArrowRightIcon"
                size={16}
                className="transition-transform group-hover:translate-x-1"
              />
            </a>
            <a
              href={localizePath('/#services')}
              className="group flex min-h-[48px] items-center justify-center gap-3 border-2 border-primary px-8 py-4 text-sm font-bold uppercase tracking-widest text-primary transition-all hover:bg-primary hover:text-white"
            >
              {copy.secondaryCta}
              <Icon
                name="ChevronDownIcon"
                size={16}
                className="transition-transform group-hover:translate-y-1"
              />
            </a>
          </div>

          <div className="h-[1px] w-full bg-gold/30" />
        </div>
      </div>

      <div className="absolute bottom-12 left-1/2 -translate-x-1/2 animate-bounce">
        <Icon name="ChevronDownIcon" size={24} className="text-gold" />
      </div>
    </section>
  );
}
